import { parseIntWithDefault } from 'src/app/shared/util/parse-int-with-default';
import {
  CaFullFormExternalData,
  CaFullFormField,
  CaFullFormValue,
  CaType,
} from './ca-full-form.types';
import { Lang } from 'src/app/shared/services/lang/lang.types';
import { ProfileType } from 'src/app/shared/entities/profile-type';
import { CryptoTokenType } from 'src/app/shared/entities/crypto-token-type';
import { ValidationConstants } from 'src/app/shared/validators/validation-constants';

function isNotCaTypeIca(
  reason: string,
): (formValue: CaFullFormValue) => string | null {
  return formValue =>
    formValue[CaFullFormField.caType] !== CaType.ICA ? reason : null;
}

// The token name used for creation of the form
function cryptoTokenNameValue(formValue: CaFullFormValue): string | null {
  // If the value of cryptoTokenName is non-empty, it's the token name.
  if (formValue[CaFullFormField.cryptoTokenName]) {
    return formValue[CaFullFormField.cryptoTokenName];
  }
  // Otherwise, if the value of profileName is non-empty, it's used as the token name.
  if (formValue[CaFullFormField.profileName]) {
    return formValue[CaFullFormField.profileName];
  }
  // Otherwise, the CN part of the DN is used as the token name.
  const dn = formValue[CaFullFormField.dn];
  if (!dn) return null;
  const cn = dn.match(/cn=([^,]*),/i)?.[1];
  if (!cn) return null;
  return cn;
}

function caIsNotRootOrCryptoTokenIsSoftAndExists(reasons: {
  onlySoftTokensForRootCaRequirePassword: string;
  tokenExists: (tokenName: string) => string;
}): (
  formValue: CaFullFormValue,
  params: { externalData: CaFullFormExternalData },
) => string | null {
  return (formValue, { externalData }) => {
    const caType = formValue[CaFullFormField.caType];
    if (caType !== CaType.Root) {
      return reasons.onlySoftTokensForRootCaRequirePassword;
    }
    const cryptotokenType = formValue[CaFullFormField.cryptoTokenType];
    if (cryptotokenType !== CryptoTokenType.SOFT) {
      return reasons.onlySoftTokensForRootCaRequirePassword;
    }
    const cryptoTokenName = cryptoTokenNameValue(formValue);
    const cryptoTokenExists =
      cryptoTokenName &&
      !!externalData.cryptotokens.find(
        token => token.tokenName === cryptoTokenName,
      );
    if (cryptoTokenExists) {
      return reasons.tokenExists(cryptoTokenName);
    }
    return null;
  };
}

export interface ConfigParams {
  lang: Lang;
}

export interface Config {
  minValidityYears: number;
  maxValidityYears: number;
  maxCertificateMaxValidityYears: (formValue: CaFullFormValue) => number;
  disabledReason: Partial<
    Record<
      CaFullFormField,
      (
        formValue: CaFullFormValue,
        params: { externalData: CaFullFormExternalData },
      ) => string | null
    >
  >;
  caCertificateProfileOptions: (
    data: CaFullFormExternalData,
  ) => (formValue: CaFullFormValue) => string[];
  signedByOptions: (data: CaFullFormExternalData) => string[];
  cryptoTokenNameOptions: (
    data: CaFullFormExternalData,
  ) => (formValue: CaFullFormValue) => string[];
}

const DAYS_IN_YEAR = 365.2422;

export const CONFIG = (params: ConfigParams): Config => {
  const { disabledReasons } = params.lang.features.provisionOperationalCa;
  return {
    minValidityYears: Math.ceil(
      ValidationConstants.minCaValidityDays / DAYS_IN_YEAR,
    ),
    maxValidityYears: Math.floor(
      ValidationConstants.maxCaValidityDays / DAYS_IN_YEAR,
    ),
    maxCertificateMaxValidityYears: formValue =>
      parseIntWithDefault(formValue[CaFullFormField.validityYears], 30),
    disabledReason: {
      [CaFullFormField.signedBy]: isNotCaTypeIca(
        disabledReasons.signedByForNotICA,
      ),
      [CaFullFormField.certificateMaxValidityYears]: isNotCaTypeIca(
        disabledReasons.certificateMaxValidityForNotICA,
      ),
      [CaFullFormField.password]:
        caIsNotRootOrCryptoTokenIsSoftAndExists(disabledReasons),
    },
    caCertificateProfileOptions: data => {
      const { profiles } = data;
      const profilesForRootCa = profiles
        .filter(profile => profile.profileType === ProfileType.ROOT_CA)
        .map(profile => profile.profileName ?? '???');
      const profilesForIca = profiles
        .filter(profile => profile.profileType === ProfileType.SUB_CA)
        .map(profile => profile.profileName ?? '???');
      const profilesByCaType: Record<string, string[]> = {
        [CaType.ICA]: profilesForIca,
        [CaType.Root]: profilesForRootCa,
      };
      return formValue =>
        profilesByCaType[formValue[CaFullFormField.caType] ?? ''] ?? [];
    },
    signedByOptions: data => data.cas.map(ca => ca.caName ?? '???'),
    cryptoTokenNameOptions: data => {
      const { cryptotokens } = data;
      const cryptotokensByType = new Map<string, string[]>();
      Object.values(CryptoTokenType).forEach(tokenType => {
        cryptotokensByType.set(
          tokenType,
          cryptotokens
            .filter(cryptotoken => cryptotoken.tokenType === tokenType)
            .map(cryptotoken => cryptotoken.tokenName ?? '???'),
        );
      });
      return formValue =>
        cryptotokensByType.get(
          formValue[CaFullFormField.cryptoTokenType] ?? '',
        ) ?? [];
    },
  };
};
